// Synthesised car sounds (Web Audio, no assets): engine crank + idle loop, lock chirp,
// indicator relay, window motor, boot latch, blower. Everything is created lazily on the
// first user gesture, because browsers refuse to start an AudioContext before one.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function ac(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain(); master.gain.value = 0.6; master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function noiseBuffer(c: AudioContext, seconds = 2): AudioBuffer {
  const b = c.createBuffer(1, c.sampleRate * seconds, c.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return b;
}

function beep(c: AudioContext, t: number, freq: number, dur: number, vol = 0.25, type: OscillatorType = "square") {
  const o = c.createOscillator(); const g = c.createGain();
  o.type = type; o.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(vol, t + 0.005); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  o.connect(g).connect(master!); o.start(t); o.stop(t + dur + 0.02);
}

/** Two-tone horn chirp on lock (one short) / unlock (two). */
export function chirp(times: 1 | 2) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  for (let i = 0; i < times; i++) { beep(c, t + i * 0.18, 1760, 0.09, 0.18, "square"); beep(c, t + i * 0.18, 2637, 0.09, 0.10, "square"); }
}

/** Indicator relay tick (call on each flash edge). */
export function relayTick(on: boolean) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const o = c.createOscillator(); const g = c.createGain();
  o.type = "square"; o.frequency.value = on ? 900 : 600;
  g.gain.setValueAtTime(0.08, t); g.gain.exponentialRampToValueAtTime(0.001, t + 0.035);
  o.connect(g).connect(master!); o.start(t); o.stop(t + 0.05);
}

/** Window motor whirr for `seconds` (filtered noise + hum). */
export function windowMotor(seconds: number) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  const src = c.createBufferSource(); src.buffer = noiseBuffer(c); src.loop = true;
  const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 420; bp.Q.value = 2.5;
  const hum = c.createOscillator(); hum.type = "sawtooth"; hum.frequency.value = 95;
  const g = c.createGain(); g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.08);
  g.gain.setValueAtTime(0.12, t + seconds - 0.1); g.gain.linearRampToValueAtTime(0, t + seconds);
  const hg = c.createGain(); hg.gain.value = 0.04;
  src.connect(bp).connect(g).connect(master!); hum.connect(hg).connect(g);
  src.start(t); hum.start(t); src.stop(t + seconds + 0.05); hum.stop(t + seconds + 0.05);
  // end-of-travel thunk
  beep(c, t + seconds, 70, 0.12, 0.2, "sine");
}

/** Boot latch release: solenoid click + lid pop. */
export function bootLatch() {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  beep(c, t, 1200, 0.03, 0.15, "square"); beep(c, t + 0.06, 90, 0.18, 0.3, "sine");
}

/** Door open (latch click) / close (thud). */
export function doorThud(open: boolean) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  if (open) { beep(c, t, 1400, 0.025, 0.12, "square"); beep(c, t + 0.04, 300, 0.08, 0.15, "triangle"); }
  else { beep(c, t, 110, 0.14, 0.35, "sine"); beep(c, t + 0.02, 55, 0.2, 0.25, "sine"); }
}

// ---------- engine ----------
let engine: { g: GainNode; oscs: OscillatorNode[]; lfo: OscillatorNode; noise: AudioBufferSourceNode } | null = null;

function idleStart(c: AudioContext, at: number) {
  if (engine) return engine;
  const g = c.createGain(); g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(0.22, at + 0.6);
  const lp = c.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 220;
  // K12M at ~800 rpm: 4 cyl → firing ~27 Hz; layer fundamentals + harmonics
  const oscs = [26.7, 53.4, 80, 106.8].map((f, i) => {
    const o = c.createOscillator(); o.type = i === 0 ? "sawtooth" : "triangle"; o.frequency.value = f;
    const og = c.createGain(); og.gain.value = [0.5, 0.35, 0.18, 0.1][i];
    o.connect(og).connect(lp); o.start(at); return o;
  });
  const lfo = c.createOscillator(); lfo.frequency.value = 3.2; const lg = c.createGain(); lg.gain.value = 1.4;
  lfo.connect(lg).connect(oscs[0].frequency); lfo.start(at);
  const noise = c.createBufferSource(); noise.buffer = noiseBuffer(c); noise.loop = true;
  const nb = c.createBiquadFilter(); nb.type = "lowpass"; nb.frequency.value = 160;
  const ng = c.createGain(); ng.gain.value = 0.35; noise.connect(nb).connect(ng).connect(lp); noise.start(at);
  lp.connect(g).connect(master!);
  engine = { g, oscs, lfo, noise };
  return engine;
}

/** Starter crank (~1.2 s) then settle into the idle loop. */
export function engineStart() {
  const c = ac(); if (!c || engine) return;
  const t = c.currentTime;
  // starter motor: rising whine with cranking pulses
  const w = c.createOscillator(); w.type = "sawtooth"; w.frequency.setValueAtTime(120, t); w.frequency.linearRampToValueAtTime(190, t + 1.1);
  const wg = c.createGain(); wg.gain.setValueAtTime(0.0, t); wg.gain.linearRampToValueAtTime(0.10, t + 0.05); wg.gain.setValueAtTime(0.10, t + 1.05); wg.gain.linearRampToValueAtTime(0, t + 1.2);
  const wf = c.createBiquadFilter(); wf.type = "bandpass"; wf.frequency.value = 700; wf.Q.value = 1.2;
  w.connect(wf).connect(wg).connect(master!); w.start(t); w.stop(t + 1.25);
  for (let i = 0; i < 6; i++) beep(c, t + 0.1 + i * 0.17, 60, 0.09, 0.28, "sine");
  // catch: rev flare then idle
  const e = idleStart(c, t + 1.15);
  if (e) {
    e.oscs.forEach((o, i) => { const base = [26.7, 53.4, 80, 106.8][i]; o.frequency.setValueAtTime(base * 1.8, t + 1.15); o.frequency.exponentialRampToValueAtTime(base, t + 2.4); });
  }
}

export function engineStop() {
  const c = ac(); if (!c || !engine) return;
  const t = c.currentTime; const e = engine; engine = null;
  e.g.gain.cancelScheduledValues(t); e.g.gain.setValueAtTime(e.g.gain.value, t); e.g.gain.linearRampToValueAtTime(0, t + 0.5);
  e.oscs.forEach((o) => { o.frequency.exponentialRampToValueAtTime(8, t + 0.5); o.stop(t + 0.6); });
  e.lfo.stop(t + 0.6); e.noise.stop(t + 0.6);
}

export function engineRunning(): boolean { return !!engine; }

// ---------- blower ----------
let blower: { g: GainNode; f: BiquadFilterNode; src: AudioBufferSourceNode } | null = null;
export function blowerLevel(level: 0 | 1 | 2 | 3 | 4) {
  const c = ac(); if (!c) return;
  const t = c.currentTime;
  if (level === 0) {
    if (blower) { const b = blower; blower = null; b.g.gain.linearRampToValueAtTime(0, t + 0.4); b.src.stop(t + 0.5); }
    return;
  }
  if (!blower) {
    const src = c.createBufferSource(); src.buffer = noiseBuffer(c, 3); src.loop = true;
    const f = c.createBiquadFilter(); f.type = "lowpass"; const g = c.createGain(); g.gain.value = 0;
    src.connect(f).connect(g).connect(master!); src.start(t); blower = { g, f, src };
  }
  blower.f.frequency.linearRampToValueAtTime(600 + level * 500, t + 0.4);
  blower.g.gain.linearRampToValueAtTime(0.03 + level * 0.03, t + 0.4);
}

/** Abort: everything silent immediately. */
export function silence() {
  engineStop(); blowerLevel(0);
}
